import React from 'react';

import dayjs from '~/timeInit';

import { Box, Button, Checkbox, Chip, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import { getTargetIdValue } from './changeMetadata';
import { PARTNER_PROPERTY_COLUMN_WIDTH, STATUS_CHIP_COLOR, TAB_CHIP_COLORS } from './constants';
import { getExtranetApprovalLabel } from './extranetApprovalLabels';
import type { ChangeMetadata } from './types';
import ValueDiff from './valueDiff/ValueDiff';

interface ChangeRowProps {
  row: ExtranetPendingChange;
  metadata?: ChangeMetadata;
  showActions: boolean;
  showStatus: boolean;
  selected: boolean;
  isMutating: boolean;
  onToggleSelect: () => void;
  onViewDiff: () => void;
  onAction: (type: 'approve' | 'reject') => void;
}

export default function ChangeRow({
  row,
  metadata,
  showActions,
  showStatus,
  selected,
  isMutating,
  onToggleSelect,
  onViewDiff,
  onAction,
}: ChangeRowProps) {
  const tabLabel = getExtranetApprovalLabel(row);
  const tabStyle = TAB_CHIP_COLORS[tabLabel] ?? {};
  const propertyId = getTargetIdValue(row.target_ids, 'property_id');
  const roomContext = [metadata?.roomName, metadata?.ratePlanName].filter(Boolean).join(' · ');
  const requestedByLabel = metadata?.requestedByEmail ?? row.requested_by_user_id;
  const requestedByName = metadata?.requestedByName;
  const reviewedByLabel = metadata?.reviewedByEmail ?? row.reviewed_by_user_id;
  const reviewedByName = metadata?.reviewedByName;

  return (
    <TableRow hover sx={{ verticalAlign: 'middle' }}>
      {showActions && (
        <TableCell padding="checkbox" sx={{ py: 1 }}>
          {row.status === 'pending' && <Checkbox checked={selected} onChange={onToggleSelect} size="small" />}
        </TableCell>
      )}
      {showActions && (
        <TableCell sx={{ py: 1 }}>
          {row.status === 'pending' ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button size="small" variant="contained" disabled={isMutating} onClick={() => onAction('approve')}>
                Approve
              </Button>
              <Button
                size="small"
                variant="text"
                color="error"
                disabled={isMutating}
                onClick={() => onAction('reject')}
              >
                Reject
              </Button>
            </Stack>
          ) : (
            <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status]} />
          )}
        </TableCell>
      )}
      <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
        <Typography variant="body2">{dayjs(row.created_at).format('D/M/YY, h:mm a')}</Typography>
      </TableCell>
      <TableCell
        sx={{
          py: 1,
          width: PARTNER_PROPERTY_COLUMN_WIDTH,
          maxWidth: PARTNER_PROPERTY_COLUMN_WIDTH,
          overflowWrap: 'anywhere',
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
          {metadata?.vendorName ?? row.fk_vendor_id}
        </Typography>
        {metadata?.vendorName && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: 'block' }}>
            {row.fk_vendor_id}
          </Typography>
        )}
        <Tooltip title={propertyId ?? ''}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: 'block' }}>
            {metadata?.propertyName ?? propertyId ?? '—'}
          </Typography>
        </Tooltip>
        {roomContext && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: 'block' }}>
            {roomContext}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 1, overflowWrap: 'anywhere' }}>
        <Tooltip title={row.requested_by_user_id}>
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {requestedByLabel}
          </Typography>
        </Tooltip>
        {requestedByName && (
          <Typography variant="caption" color="text.secondary" display="block">
            {requestedByName}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <Chip size="small" label={tabLabel} sx={{ ...tabStyle, fontWeight: 600, fontSize: 11 }} />
      </TableCell>
      <TableCell sx={{ maxWidth: 400, py: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              maxHeight: 80,
              overflow: 'auto',
              flex: 1,
            }}
          >
            <ValueDiff previous={row.previous_value} proposed={row.proposed_value} compact />
          </Box>
          <Button
            size="small"
            variant="text"
            sx={{ px: 0, textTransform: 'uppercase', fontSize: 11, whiteSpace: 'nowrap', minWidth: 'auto' }}
            onClick={onViewDiff}
          >
            View diff
          </Button>
        </Stack>
      </TableCell>
      {showStatus && (
        <TableCell sx={{ py: 1, overflow: 'hidden' }}>
          <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status]} />
          {row.reviewed_by_user_id && (
            <Tooltip title={row.reviewed_by_user_id}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                noWrap
                sx={{ fontSize: 10, mt: 0.5 }}
              >
                by {reviewedByLabel}
              </Typography>
            </Tooltip>
          )}
          {reviewedByName && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
              {reviewedByName}
            </Typography>
          )}
          {row.reviewed_at && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>
              {dayjs(row.reviewed_at).format('D/M/YY')}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
